import {
  skipToken,
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";
import { downloadZip } from "client-zip";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { P, match } from "ts-pattern";
import type {
  CogInputSchema,
  ErrorWithJSON,
  Model,
  Prediction,
  Version,
} from "../../types";
import { route } from "../../urls";
import { fetchAndRenderMarkdown, getPrediction } from "./api";
import {
  PREDICTION_REFETCH_BACKOFF_FACTOR,
  PREDICTION_REFETCH_BASE_SETUP_INTERVAL,
  PREDICTION_REFETCH_MAX_INTERVAL,
  PREDICTION_REFETCH_PROCESSING_INTERVAL,
  TERMINAL_PREDICTION_STATUSES,
} from "./constants";

export const queryKeys = {
  predictions: {
    uuid: (uuid: string | null) => ["predictions", uuid],
  },
  versions: {
    uuid: (uuid: string) => ["versions", uuid],
  },
};

export function calculatePredictionRefetchSetupInterval(
  runCount: number
): number {
  if (runCount < 2) {
    return 1;
  }

  return Math.min(
    PREDICTION_REFETCH_BASE_SETUP_INTERVAL *
      PREDICTION_REFETCH_BACKOFF_FACTOR ** (runCount - 2),
    PREDICTION_REFETCH_MAX_INTERVAL
  );
}

export function usePrediction({
  uuid,
}: {
  uuid: string | null;
}): UseQueryResult<Prediction, ErrorWithJSON> {
  return useQuery<Prediction, ErrorWithJSON, Prediction>({
    enabled: Boolean(uuid),
    refetchOnWindowFocus(query) {
      const lastKnownStatus = query.state.data?.status;
      if (!lastKnownStatus) return false;
      return !TERMINAL_PREDICTION_STATUSES.includes(lastKnownStatus);
    },
    refetchIntervalInBackground: true,
    refetchInterval: (query) =>
      match(query.state.data?.status)
        .with(
          P.nullish,
          P.union("canceled", "failed", "succeeded", "aborted"),
          () => false as const
        )
        .with("processing", () => PREDICTION_REFETCH_PROCESSING_INTERVAL)
        .with(P.union("canceling", "starting"), () =>
          calculatePredictionRefetchSetupInterval(query.state.dataUpdateCount)
        )
        .exhaustive(),
    queryKey: queryKeys.predictions.uuid(uuid),
    queryFn: ({ signal }) => getPrediction({ uuid, signal }),
    // By default, initial query data is treated as totally fresh, as if it were just fetched.
    // By setting a staleTime that matches the refetchInterval, we can effectively delay a subsequent
    // refetch until the staleTime has passed, at which point our refetchInterval will kick in
    // and decide whether or not to refetch based on the prediction status.
    staleTime: PREDICTION_REFETCH_PROCESSING_INTERVAL,
  });
}

export function useInputsForm(
  properties: CogInputSchema["properties"],
  initialInputs?: Record<string, any>
) {
  // Get supported query string parameters. This needs to be kept in sync with
  // `needs_initial_prediction` check in `model_detail` view.
  const prefill = useMemo(() => {
    const prefill: Record<string, unknown> = {};
    const params = new URLSearchParams(window.location.search);

    // We don't want to pre-fill if we're loading an existing prediction regardless
    // of any other query strings.
    if (params.has("prediction")) {
      return prefill;
    }

    params.forEach((value, key) => {
      if (key in properties) {
        prefill[key] = value;
      }
    });

    return prefill;
  }, [properties]);

  return useForm<any>({
    mode: "onChange",
    defaultValues: Object.keys(properties).reduce((acc, name) => {
      const property = properties[name];
      const hasType = "type" in property;

      const defaultInputs = Object.fromEntries(
        Object.entries(properties)
          .filter(([_, value]) => "default" in value)
          .map(([key, value]) => [key, value.default])
      );

      // Merge initialInputs with query params, giving priority to query params
      const values = { ...initialInputs, ...prefill } || defaultInputs;

      if (values && name in values) {
        // "anyOf" fields won't have a type, so we'll naïvely just use the initial value.
        if (!hasType) {
          const val = values[name];
          acc[name] = val;
          return acc;
        }

        const propertyType = property.type;

        let val = values[name];

        // We want to make sure we any integers or number initial inputs are cast to numbers.
        if (propertyType === "integer") {
          val = Number.parseInt(val);
        } else if (propertyType === "number") {
          val = Number.parseFloat(val);
        } else if (
          propertyType === "array" &&
          property.items.type === "string" &&
          Array.isArray(val)
        ) {
          val = val.map((v) => ({ value: v }));
        }

        // Initialize secret fields to be blank.
        if (propertyType === "string" && property.format === "password") {
          val = "";
        }

        acc[name] = val;
      } else if ("default" in property) {
        acc[name] = property.default;
      } else if ("enum" in property && property.enum?.length) {
        // To match native select behaviour, if there are any choices, pick the
        // first by default.
        acc[name] = property.enum[0];
      } else {
        acc[name] = null;
      }
      return acc;
    }, {}),
  });
}

function parseExtension(url: string) {
  const parts = url.split(".");
  return parts.length > 1 ? `.${parts[parts.length - 1]}` : "";
}

export function useDownloadOutput(prediction: Prediction) {
  const outputFiles = prediction._extras.output_files;
  const id = prediction.id;

  const stringifiedUrls = useMemo(
    () =>
      JSON.stringify(
        outputFiles.filter((url) => url && typeof url === "string")
      ),
    [outputFiles]
  );

  const [isEmpty, setIsEmpty] = useState(true);
  const [isPreparing, setIsPreparing] = useState(false);

  const download = useCallback(async () => {
    if (isEmpty || isPreparing) {
      return;
    }

    const urls = JSON.parse(stringifiedUrls);

    if (!urls || urls.length === 0) {
      return;
    }

    setIsPreparing(true);

    const prefix = `replicate-prediction-${id}`;
    const anchor = document.createElement("a");

    let blob: Blob;
    let filename: string;

    try {
      if (urls.length === 1) {
        const url = urls[0];
        const res = await fetch(url);
        blob = await res.blob();
        filename = `${prefix}${parseExtension(url)}`;
      } else {
        const filesToZip = await Promise.all(
          urls.map(async (url, i) => ({
            name: `${prefix}-${i}${parseExtension(url)}`,
            input: await fetch(url),
          }))
        );
        const res = downloadZip(filesToZip);
        blob = await res.blob();
        filename = `${prefix}.zip`;
      }

      anchor.href = URL.createObjectURL(blob);
      anchor.download = filename;
      anchor.click();
      anchor.remove();
      setIsPreparing(false);
    } catch (e) {
      setIsPreparing(false);
      toast.error(`Download failed: ${(e as Error).message}`);
    }
  }, [isEmpty, isPreparing, id, stringifiedUrls]);

  useEffect(() => {
    if (!["canceled", "failed", "succeeded"].includes(prediction.status)) {
      setIsEmpty(true);
      return;
    }

    const urls = JSON.parse(stringifiedUrls);

    if (!urls || urls.length === 0) {
      setIsEmpty(true);
      return;
    }

    setIsEmpty(false);
  }, [prediction.status, stringifiedUrls]);

  return {
    isEmpty,
    isPreparing,
    download,
  };
}

export function useRemoteMarkdown({ url }: { url: string }) {
  return useQuery({
    queryKey: ["markdown", url],
    queryFn: () => fetchAndRenderMarkdown({ url }),
    refetchOnWindowFocus: false,
  });
}

export function usePredictionOutputShouldStream(prediction: Prediction | null):
  | {
      predictionOutputShouldStream: true;
      predictionOutputStreamUrl: string;
    }
  | {
      predictionOutputShouldStream: false;
      predictionOutputStreamUrl: null;
    } {
  if (!prediction?.urls.stream) {
    return {
      predictionOutputShouldStream: false,
      predictionOutputStreamUrl: null,
    };
  }

  return {
    predictionOutputShouldStream: true,
    predictionOutputStreamUrl: prediction.urls.stream,
  };
}

interface DockerImage {
  docker_image_name: string;
}

export function useDockerImage(version: Version) {
  return useQuery({
    ...useQueryOnceOptions,
    queryKey: ["api_version_docker_image", version.id],
    queryFn: () =>
      genericFetch<DockerImage>({
        failureReason: "Failed to fetch version docker image",
        url: route("api_version_docker_image", {
          username: version._extras.model.owner,
          name: version._extras.model.name,
          docker_image_id: version.id,
        }),
      }),
  });
}

interface PredictionPrice {
  average_prediction_price: string | null;
}

export function usePredictionPrice(version: Version | null) {
  return useQuery({
    ...useQueryOnceOptions,
    enabled: Boolean(version),
    queryKey: ["api_version_prediction_price", version?.id],
    queryFn: !version
      ? skipToken
      : () =>
          genericFetch<PredictionPrice>({
            failureReason: "Failed to fetch version prediction price",
            url: route("api_version_prediction_price", {
              username: version._extras.model.owner,
              name: version._extras.model.name,
              docker_image_id: version.id,
            }),
          }),
  });
}

export interface Capabilities {
  hotswap: boolean;
  run: boolean;
  stream: boolean;
  train: boolean;
}

export function useVersionCapabilities(
  version: Version
): UseQueryResult<Capabilities, ErrorWithJSON> {
  return useQuery({
    ...useQueryOnceOptions,
    queryKey: ["api_version_capabilities", version.id],
    queryFn: () =>
      genericFetch<Capabilities>({
        failureReason: "Failed to fetch version capabilities",
        url: route("api_version_capabilities", {
          username: version._extras.model.owner,
          name: version._extras.model.name,
          docker_image_id: version.id,
        }),
      }),
  });
}

async function genericFetch<T>({
  url,
  failureReason,
}: { url: string; failureReason: string }): Promise<T> {
  const res = await fetch(url);

  if (res.ok) {
    return res.json<T>();
  }

  let error = {
    detail: `${failureReason}: ${res.statusText}`,
    status: res.status,
  };
  try {
    error = {
      ...error,
      ...(await res.json<ErrorWithJSON>()),
    };
  } catch (e) {}

  return Promise.reject(error);
}

interface InputHints {
  trigger_word: string | null;
}

export function useVersionedInputHints({
  owner,
  name,
  versionId,
}: { owner: string; name: string; versionId: string }): UseQueryResult<
  InputHints,
  ErrorWithJSON
> {
  return useQuery({
    ...useQueryOnceOptions,
    queryKey: ["api_version_input_hints", versionId],
    queryFn: () =>
      genericFetch<InputHints>({
        failureReason: "Failed to fetch version input hints",
        url: route("api_version_input_hints", {
          username: owner,
          name: name,
          docker_image_id: versionId,
        }),
      }),
  });
}

export function useVersionlessInputHints({
  owner,
  name,
}: { owner: string; name: string }) {
  return useQuery({
    ...useQueryOnceOptions,
    queryKey: ["api_model_input_hints", owner, name],
    queryFn: () => {
      return genericFetch<InputHints>({
        url: route("api_model_input_hints", {
          username: owner,
          name: name,
        }),
        failureReason: "Failed to fetch model input hints",
      });
    },
  });
}

const useQueryOnceOptions = {
  gcTime: Number.POSITIVE_INFINITY,
  staleTime: Number.POSITIVE_INFINITY,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  refetchInterval: false,
  refetchIntervalInBackground: false,
  retry: false,
} satisfies Partial<UseQueryOptions>;
